import { useEffect, useRef } from 'react'
import { init, dispose } from 'klinecharts'
import type { Chart } from 'klinecharts'
import { DatafeedSocket } from '@/bot/adapters/datafeedSocket'
import { registerWorkbenchOverlays } from './customOverlays'
import { registerVolumeOverlay } from './volumeOverlay'
import { buildStyles, defaultBackground } from './chartStyles'
import { chartTypeById } from './catalog'
import { CanvasSeries } from './canvasSeries'
import { useIsDark } from './useIsDark'
import type { ChartSettings, ChartTypeId } from './types'

export interface WorkbenchChartStatus {
  loading: boolean
  error: string | null
  empty: boolean
  /** The symbol this status belongs to — lets the orchestrator's per-symbol
   * restore ignore a stale load that lands after another symbol switch. */
  symbol?: string
}

export interface WorkbenchChartProps {
  symbol: string
  timeframe: string
  /** Anchor (unix seconds) for the most recent candle to show; null = now. */
  anchorTs: number | null
  /** Decimal places for prices (from the symbol's point size); null = chart default. */
  pricePrecision: number | null
  chartType: ChartTypeId
  settings: ChartSettings
  /**
   * Hands the raw klinecharts instance up on init, with the candle series that owns its data
   * (see canvasSeries.ts — the walkthrough's bar replay drives it); both null again on dispose.
   */
  onReady: (chart: Chart | null, series: CanvasSeries | null) => void
  onStatusChange?: (status: WorkbenchChartStatus) => void
}

/**
 * klinecharts prints a dev-only "Welcome to klinecharts" banner synchronously
 * inside init() with no option to disable it — mute console.log for just that
 * one line while init() runs.
 */
function initQuietly(el: HTMLElement): Chart | null {
  const nativeLog = console.log
  console.log = (...args: unknown[]) => {
    if (typeof args[0] === 'string' && args[0].includes('Welcome to klinecharts')) return
    nativeLog(...args)
  }
  try {
    return init(el)
  } finally {
    console.log = nativeLog
  }
}

/**
 * The klinecharts engine wrapper. Owns init/dispose, overlay/indicator
 * registrations, the candle series (canvasSeries.ts: loading over the datafeed
 * WebSocket, pagination both ways, and the walkthrough replay's cutoff), price
 * precision, and the theme/settings/chart-type styling. Everything else (tools, indicators, drawings) is driven by the
 * orchestrator through the raw chart instance handed up via `onReady` — the
 * chart is never torn down on symbol/type switches, so overlays and indicators
 * survive them.
 */
export function WorkbenchChart({
  symbol,
  timeframe,
  anchorTs,
  pricePrecision,
  chartType,
  settings,
  onReady,
  onStatusChange,
}: WorkbenchChartProps) {
  const isDark = useIsDark()

  const containerRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<Chart | null>(null)
  const socketRef = useRef<DatafeedSocket | null>(null)
  const seriesRef = useRef<CanvasSeries | null>(null)

  // Latest values for use inside long-lived chart callbacks.
  const onStatusRef = useRef(onStatusChange)
  const onReadyRef = useRef(onReady)
  useEffect(() => {
    onStatusRef.current = onStatusChange
    onReadyRef.current = onReady
  })

  // ---- Init / dispose -------------------------------------------------------
  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    registerWorkbenchOverlays() // no-op after first call
    registerVolumeOverlay()
    const chart = initQuietly(el)
    chartRef.current = chart
    try {
      socketRef.current = new DatafeedSocket()
    } catch (err) {
      onStatusRef.current?.({
        loading: false,
        error: err instanceof Error ? err.message : 'Datafeed not configured',
        empty: true,
      })
    }

    // The series is the ONLY writer of the engine's data list: loads, pagination (it answers
    // the engine's load-data callback in both directions) and the replay's cutoff.
    const series = chart
      ? new CanvasSeries({ chart, socket: socketRef.current, heikinAshi: chartTypeById(chartType).ha })
      : null
    seriesRef.current = series
    const unsubscribe = series?.subscribe((e) => {
      if (e.type === 'status') onStatusRef.current?.(e.status)
    })
    if (chart && series) chart.setLoadDataCallback(series.handleLoadData)

    // klinecharts sizes its canvases once at init — follow container resizes
    // (window, chat-panel drag handle, sidebar collapse) explicitly.
    const ro = new ResizeObserver(() => chartRef.current?.resize())
    ro.observe(el)

    onReadyRef.current(chart, series)

    return () => {
      ro.disconnect()
      onReadyRef.current(null, null)
      unsubscribe?.()
      series?.dispose()
      seriesRef.current = null
      socketRef.current?.dispose()
      socketRef.current = null
      dispose(el)
      chartRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ---- Theme / settings / chart-type styling --------------------------------
  useEffect(() => {
    chartRef.current?.setStyles(buildStyles(isDark, settings, chartTypeById(chartType).engine))
  }, [isDark, settings, chartType])

  // ---- Price precision --------------------------------------------------------
  // Drives both the y-axis auto-scale (klinecharts pads the range by 10^-precision
  // when the visible spread is tiny — a too-coarse default squashes quiet windows
  // into a thin strip) and the y-axis tick / price-mark formatting.
  useEffect(() => {
    const chart = chartRef.current
    if (!chart || pricePrecision == null) return
    chart.setPriceVolumePrecision(pricePrecision, 0)
  }, [pricePrecision])

  // ---- Initial load on symbol / timeframe / anchor change --------------------
  useEffect(() => {
    if (!symbol) return
    void seriesRef.current?.load({ symbol, timeframe, anchorTs })
  }, [symbol, timeframe, anchorTs])

  // ---- Re-shape data on chart-type change ------------------------------------
  // Only Heikin Ashi transforms the raw bars; bar / candle / line share identical
  // OHLC data and differ purely in the engine candle type, which the styling
  // effect above already applies via setStyles. So re-apply data ONLY when the HA
  // flag flips — an unconditional applyNewData resets the scroll offset and snaps
  // the view back to the latest bar, discarding the range the user is looking at.
  // The series keeps the viewport across the flip (see CanvasSeries.setHeikinAshi).
  useEffect(() => {
    seriesRef.current?.setHeikinAshi(chartTypeById(chartType).ha)
  }, [chartType])

  return (
    <div
      ref={containerRef}
      className="absolute inset-0"
      style={{ backgroundColor: settings.background || defaultBackground(isDark) }}
    />
  )
}
